/**
 * Training-boost ledger: every speed-up to cultivation and neigong training in one place.
 * Each source (place, dance, duel, item) has ONE slot holding a bonus; slots ADD (never multiply)
 * and the sum is capped at +MAX_BONUS (x3 overall). After SOFT_CAP boosted points in one day the
 * bonus halves. Pure: time comes in as dt, the day as a number from the caller (a later piece
 * supplies the real-day number; a new day resets the counter).
 */
export type BoostSource = 'place' | 'dance' | 'duel' | 'item' | 'sectDance';
export const BOOST_SOURCES: readonly BoostSource[] = ['place', 'dance', 'duel', 'item', 'sectDance'];
/** H23: a source that counts for one neigong category only (the sect dance: school neigong). Others count for all. */
export const BOOST_ONLY: Partial<Record<BoostSource, 'jianghu' | 'school'>> = { sectDance: 'school' };
export const TRAIN_BOOST = { maxBonus: 2, softCap: 3000, softFactor: 0.5 } as const;

/** Sum of bonuses (each >= 0), capped. Order-independent. */
export function boostTotal(bonuses: Partial<Record<BoostSource, number>>, category?: 'jianghu' | 'school'): number {
  let s = 0;
  for (const k of BOOST_SOURCES) if (!BOOST_ONLY[k] || BOOST_ONLY[k] === category) s += Math.max(0, bonuses[k] ?? 0);
  return Math.min(TRAIN_BOOST.maxBonus, s);
}

/** The rate multiplier (1 + capped sum) for training speed. */
export function boostMult(bonuses: Partial<Record<BoostSource, number>>): number {
  return 1 + boostTotal(bonuses);
}

export class TrainBoostLedger {
  private slots: Partial<Record<BoostSource, { bonus: number; left: number }>> = {};
  /** Day number of the counter below, and boosted points earned that day. */
  day = 0;
  boosted = 0;

  /** Sets a source's slot (refresh, never stacks with itself). `seconds` = Infinity while held. */
  set(src: BoostSource, bonus: number, seconds = Infinity): void {
    if (bonus <= 0 || seconds <= 0) delete this.slots[src];
    else this.slots[src] = { bonus, left: seconds };
  }

  clear(src: BoostSource): void {
    delete this.slots[src];
  }

  bonus(src: BoostSource): number {
    return this.slots[src]?.bonus ?? 0;
  }

  /** Counts timed slots down (online seconds); drops the ended ones. */
  tick(dt: number): void {
    for (const k of BOOST_SOURCES) {
      const s = this.slots[k];
      if (s && Number.isFinite(s.left) && (s.left -= dt) <= 0) delete this.slots[k];
    }
  }

  /** Capped sum of the slots (before the daily soft cap). */
  total(category?: 'jianghu' | 'school'): number {
    const b: Partial<Record<BoostSource, number>> = {};
    for (const k of BOOST_SOURCES) b[k] = this.bonus(k);
    return boostTotal(b, category);
  }

  /** Bonus in force on `day`: halved once the day's boosted points passed the soft cap. */
  effective(day: number): number {
    if (day !== this.day) {
      this.day = day;
      this.boosted = 0;
    }
    const t = this.total();
    return this.boosted >= TRAIN_BOOST.softCap ? t * TRAIN_BOOST.softFactor : t;
  }

  /** Points earned over `dt` seconds at `base`/s with the bonus; counts the boosted part. */
  gain(base: number, dt: number, day: number): number {
    const e = this.effective(day);
    this.boosted += base * e * dt;
    return base * (1 + e) * dt;
  }
}
